import { cn } from "@/shared/lib/cn";

/**
 * A deterministic, colorful avatar for entities without an uploaded image
 * (assistants, etc.): a hashed two-tone gradient tile with a purpose-matched
 * line glyph — never text. Same `seed` always renders the same avatar.
 *
 * Color lives in inline styles (hsl), not token utilities — this is generative
 * *content* (like a generated image or a brand logo), deliberately outside the
 * monochrome chrome palette.
 */
export interface GenerativeAvatarProps {
  /** Stable seed — drives both the colour and the glyph (use the name or id). */
  seed: string;
  /** Sizing + radius classes (e.g. "size-12 rounded-lg"). */
  className?: string;
}

function hash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return Math.abs(h);
}

type GlyphKey =
  | "code"
  | "spark"
  | "pen"
  | "lens"
  | "cap"
  | "briefcase"
  | "palette"
  | "leaf"
  | "note"
  | "bot";

// Keyword → glyph. First match in a (lower-cased) seed wins.
const KEYWORDS: [RegExp, GlyphKey][] = [
  [/cod|program|leetcode|\bdev\b|python|javascript|typescript|\bsql\b|debug|engineer|software|algorithm/, "code"],
  [/prompt|refin|enhance|optim/, "spark"],
  [/grammar|writ|essay|copy|blog|\btext\b|edit|proofread|translat|languag|\bword|spell|story|novel/, "pen"],
  [/research|analy|\bdata\b|scien|\bpaper\b|insight|report|summar/, "lens"],
  [/teach|tutor|\blearn|\bedu|coach|mentor|course|exam|\bmath|homework|quiz/, "cap"],
  [/business|market|\bsales\b|financ|account|legal|startup|\bproduct|strateg|\bhr\b|recruit/, "briefcase"],
  [/design|\bart\b|creativ|image|paint|draw|logo|\bbrand|photo|\bui\b|\bux\b/, "palette"],
  [/health|fitness|\blife|cook|recipe|\bfood|travel|wellness|medit|nutri|yoga|therap/, "leaf"],
  [/music|audio|\bsong|sound|podcast|voice/, "note"],
  [/\bbot\b|assistant|\bagent\b|\bgpt\b|\bai\b|helper|\bchat|support|companion/, "bot"],
];

const FALLBACK: GlyphKey[] = [
  "spark",
  "bot",
  "pen",
  "lens",
  "palette",
  "leaf",
  "note",
  "cap",
  "briefcase",
  "code",
];

function pickGlyph(seed: string, h: number): GlyphKey {
  const s = seed.toLowerCase();
  for (const [re, key] of KEYWORDS) if (re.test(s)) return key;
  return FALLBACK[h % FALLBACK.length];
}

const GLYPHS: Record<GlyphKey, React.ReactNode> = {
  code: <path d="m9 8-4 4 4 4M15 8l4 4-4 4" />,
  spark: (
    <>
      <path d="M12 4v5M12 15v5M4 12h5M15 12h5" />
      <path d="m7 7 2 2M15 15l2 2M17 7l-2 2M9 15l-2 2" />
    </>
  ),
  pen: <path d="M5 19 17 7a2.8 2.8 0 0 0-4-4L4 12l-1 7 7-1Z" />,
  lens: (
    <>
      <circle cx="11" cy="11" r="6" />
      <path d="m20 20-4-4" />
    </>
  ),
  cap: (
    <>
      <path d="M3 9 12 5l9 4-9 4-9-4Z" />
      <path d="M7 11.5V16c0 1.1 2.2 2 5 2s5-.9 5-2v-4.5" />
    </>
  ),
  briefcase: (
    <>
      <rect x="3" y="8" width="18" height="12" rx="2" />
      <path d="M8 8V6a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </>
  ),
  palette: (
    <>
      <path d="M12 4a8 8 0 1 0 0 16c1.1 0 1.5-.9 1.5-1.7 0-1.6 1.3-1.8 2.5-1.8A4 4 0 0 0 20 12.5 8 8 0 0 0 12 4Z" />
      <path d="M8 10h.01M12 8h.01M15.5 10h.01" />
    </>
  ),
  leaf: (
    <>
      <path d="M5 19c0-8 6-14 14-14 0 8-6 14-14 14Z" />
      <path d="M5 19c2.5-3 5.5-5 9-6.5" />
    </>
  ),
  note: (
    <>
      <path d="M9 17V6l9-2v11" />
      <circle cx="6.5" cy="17" r="2.5" />
      <circle cx="15.5" cy="15" r="2.5" />
    </>
  ),
  bot: (
    <>
      <rect x="5" y="8" width="14" height="11" rx="3" />
      <path d="M12 8V4.5M9 13h.01M15 13h.01M9.5 16h5" />
    </>
  ),
};

export function GenerativeAvatar({ seed, className }: GenerativeAvatarProps) {
  const h = hash(seed || "?");
  const hue = h % 360;
  const hue2 = (hue + 26) % 360;
  const light = h % 3 === 0; // ~1/3 get a soft light tile, the rest vivid/dark
  const bg = light
    ? `linear-gradient(140deg, hsl(${hue} 70% 88%), hsl(${hue2} 64% 78%))`
    : `linear-gradient(140deg, hsl(${hue} 58% 47%), hsl(${hue2} 64% 35%))`;
  const fg = light ? `hsl(${hue} 55% 28%)` : "hsl(0 0% 100%)";
  const glyph = pickGlyph(seed, h);

  return (
    <div
      aria-hidden="true"
      className={cn("grid shrink-0 place-items-center overflow-hidden", className)}
      style={{ backgroundImage: bg }}
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke={fg}
        strokeWidth={1.7}
        strokeLinecap="round"
        strokeLinejoin="round"
        className="size-[58%]"
      >
        {GLYPHS[glyph]}
      </svg>
    </div>
  );
}
